import { SignInPrompt } from '@/features/account/SignInPrompt';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ErrorState, LoadingState } from '@/components/ui/async-state';
import { Icon } from '@/components/ui/icon';
import { AppScreen, Card, IconAction } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { UsernameBadge } from '@/components/ui/UsernameBadge';
import type { PatchProfileRequest } from '@/lib/api/apiTypes';
import { useAppearance, type AppearancePreference } from '@/lib/design/appearance';
import { useAuth } from '@/lib/auth/auth';
import { getAPIErrorRecoveryCopy, getFirstAPIErrorFieldCopy } from '@/lib/api/apiErrorCopy';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { APP_CONFIG } from '@/lib/app/config';
import {
  EVENT_TAG_GROUPS,
  getEventTagColors,
  getEventTagLabel,
  type EventTagValue,
} from '@/features/events/eventTags';
import { trackProfileUpdated } from '@/lib/analytics/posthog';
import { captureSentryException } from '@/lib/analytics/sentry';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { showToast } from '@/lib/useToastStore';
import { useAvatarUpload } from '@/features/profile/useAvatarUpload';
import { useDashboard } from '@/features/profile/useDashboard';
import {
  ACCOUNT_VALIDATION_LIMITS,
  USERNAME_HELP_COPY,
  normalizeUsernameInput,
  validatePasswordConfirmation,
  validateProfileIdentity,
  validateUsername,
} from '@/features/account/accountValidation';
import { useSetPassword } from '@/features/account/useSetPassword';
import { useUpdateProfile } from '@/features/profile/useUpdateProfile';
import { useWorkspaceMode } from '@/features/workspaces/useWorkspaceMode';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { getPublicIdentityHref } from '@/lib/navigation/routes';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import { cn } from '@/lib/utils';
import Constants from 'expo-constants';
import * as ImagePicker from 'expo-image-picker';
import { router } from 'expo-router';
import {
  ArrowLeftIcon,
  BellRingIcon,
  BriefcaseBusinessIcon,
  CameraIcon,
  FileTextIcon,
  Globe2Icon,
  HistoryIcon,
  LockIcon,
  LogOutIcon,
  MailIcon,
  MonitorIcon,
  MoonIcon,
  PencilLineIcon,
  ShieldIcon,
  SparklesIcon,
  SunIcon,
  Trash2Icon,
} from 'lucide-react-native';
import * as React from 'react';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
  useWindowDimensions,
} from 'react-native';

const APP_VERSION = Constants.nativeAppVersion ?? Constants.expoConfig?.version ?? '0.0.0';
const EXPO_BUILD =
  Platform.OS === 'ios'
    ? Constants.expoConfig?.ios?.buildNumber
    : Constants.expoConfig?.android?.versionCode
      ? String(Constants.expoConfig.android.versionCode)
      : null;
const APP_BUILD = Constants.nativeBuildVersion ?? EXPO_BUILD;

export default function SettingsScreen() {
  const session = useAuth((s) => s.session);
  const logout = useAuth((s) => s.logout);
  const deleteAccount = useAuth((s) => s.deleteAccount);
  const isDesktop = useIsDesktop();
  const appearancePreference = useAppearance((state) => state.preference);
  const resolvedAppearance = useAppearance((state) => state.resolvedMode);
  const setAppearancePreference = useAppearance((state) => state.setPreference);
  const dashboardQuery = useDashboard();
  const workspaceMode = useWorkspaceMode();
  const { currentBusinessMembership, currentContext, meaningfulContextCount } = useAccountContext();
  const updateProfileMutation = useUpdateProfile();
  const avatarUploadMutation = useAvatarUpload();
  const setPasswordMutation = useSetPassword();
  const dashboard = dashboardQuery.data ?? null;
  const isLoading = Boolean(session) && dashboardQuery.isPending;
  const isStaff = dashboard?.profile.is_staff ?? session?.me.profile.is_staff ?? false;
  const isVenueAccount = Boolean(dashboard?.profile.is_venue);
  const showSetPassword = session?.me.user.has_password === false;
  const [error, setError] = React.useState<string | null>(null);
  const [isAppearanceVisible, setIsAppearanceVisible] = React.useState(false);
  const [isEditVisible, setIsEditVisible] = React.useState(false);
  const [isInterestsVisible, setIsInterestsVisible] = React.useState(false);
  const [isSetPasswordVisible, setIsSetPasswordVisible] = React.useState(false);
  const [draftUsername, setDraftUsername] = React.useState('');
  const [draftDisplayName, setDraftDisplayName] = React.useState('');
  const [draftBio, setDraftBio] = React.useState('');
  const [draftAvatarUrl, setDraftAvatarUrl] = React.useState<string | null>(null);
  const [draftInterestTags, setDraftInterestTags] = React.useState<string[]>([]);
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmNewPassword, setConfirmNewPassword] = React.useState('');
  const [setPasswordError, setSetPasswordError] = React.useState<string | null>(null);

  function closeSetPasswordModal() {
    setIsSetPasswordVisible(false);
    setNewPassword('');
    setConfirmNewPassword('');
    setSetPasswordError(null);
  }

  async function handleSaveProfile() {
    if (!dashboard) {
      return;
    }

    setError(null);
    try {
      const trimmedUsername = draftUsername.trim().toLowerCase();
      const trimmedDisplayName = draftDisplayName.trim();
      const trimmedBio = draftBio.trim();
      const profileError = validateProfileIdentity({
        bio: trimmedBio,
        displayName: trimmedDisplayName,
        username: trimmedUsername,
      });
      if (profileError) {
        setError(profileError);
        return;
      }

      const payload: PatchProfileRequest = {
        username: trimmedUsername,
        display_name: trimmedDisplayName,
        bio: trimmedBio,
      };
      const fieldsChanged: ('username' | 'display_name' | 'bio' | 'avatar_url')[] = [];

      if (trimmedUsername !== dashboard.profile.username) {
        fieldsChanged.push('username');
      }

      if (trimmedDisplayName !== dashboard.profile.display_name) {
        fieldsChanged.push('display_name');
      }

      if (trimmedBio !== (dashboard.profile.bio ?? '').trim()) {
        fieldsChanged.push('bio');
      }

      if (draftAvatarUrl !== null) {
        payload.avatar_url = draftAvatarUrl;
        if (draftAvatarUrl !== dashboard.profile.avatar_url) {
          fieldsChanged.push('avatar_url');
        }
      }

      await updateProfileMutation.mutateAsync(payload);
      trackProfileUpdated(fieldsChanged);
      setDraftAvatarUrl(null);
      setIsEditVisible(false);
    } catch (saveError) {
      setError(
        getFirstAPIErrorFieldCopy(saveError, ['username', 'display_name', 'bio', 'avatar_url']) ??
          getAPIErrorRecoveryCopy(saveError, 'profile', 'Unable to update profile')
      );
    }
  }

  async function handleSaveInterests() {
    setError(null);
    try {
      await updateProfileMutation.mutateAsync({ interest_tags: draftInterestTags });
      trackProfileUpdated(['interest_tags']);
      setIsInterestsVisible(false);
      showToast({ message: 'Discover will tune itself around those interests.' });
    } catch (saveError) {
      setError(
        getFirstAPIErrorFieldCopy(saveError, ['interest_tags']) ??
          getAPIErrorRecoveryCopy(saveError, 'profile', 'Unable to update interests')
      );
    }
  }

  async function handlePickAvatar() {
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      Alert.alert('Permission needed', 'Photo library access is required to change your avatar.');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.85,
      allowsEditing: true,
      aspect: [1, 1],
      shape: 'oval',
    });

    if (result.canceled || !result.assets?.[0]) {
      return;
    }

    const asset = result.assets[0];
    try {
      const data = await avatarUploadMutation.mutateAsync({
        uri: asset.uri,
        mimeType: asset.mimeType,
        fileName: asset.fileName,
      });
      setDraftAvatarUrl(data.url);
    } catch (uploadError) {
      captureSentryException(uploadError, {
        tags: { area: 'upload', asset: 'avatar' },
        extra: {
          fileName: asset.fileName ?? 'avatar.jpg',
          mimeType: asset.mimeType ?? 'image/jpeg',
        },
      });
      Alert.alert(
        'Upload failed',
        getAPIErrorRecoveryCopy(uploadError, 'avatar_upload', 'Unable to upload avatar. Please try again.')
      );
    }
  }

  async function handleSetPassword() {
    setSetPasswordError(null);

    const passwordError = validatePasswordConfirmation(newPassword, confirmNewPassword);
    if (passwordError) {
      setSetPasswordError(passwordError);
      return;
    }

    try {
      await setPasswordMutation.mutateAsync({ password: newPassword });
      closeSetPasswordModal();
      showToast({ message: 'Password set. You can now sign in with email and password.' });
    } catch (setError) {
      setSetPasswordError(
        getFirstAPIErrorFieldCopy(setError, ['password']) ??
          getAPIErrorRecoveryCopy(setError, undefined, 'Unable to set password.')
      );
    }
  }

  return (
    <AppScreen>
      <ScrollView
        contentContainerStyle={{ paddingBottom: isDesktop ? 32 : 32 }}
        showsVerticalScrollIndicator={false}>
        <View
          style={
            isDesktop
              ? { maxWidth: 760, width: '100%', alignSelf: 'center', paddingHorizontal: 32 }
              : undefined
          }>
          <View className={isDesktop ? 'pt-4' : 'px-4 pt-4'}>
            <View className="flex-row items-center justify-between">
              <IconAction icon={ArrowLeftIcon} onPress={() => router.back()} />
              <Text className="text-3xl font-extrabold tracking-tight">Settings</Text>
              <View className="size-11" />
            </View>
          </View>

          {!session ? (
            <View className={isDesktop ? 'pt-8' : 'px-4 pt-8'}>
              <SignInPrompt title="Manage your account." body="Sign in to open settings." />
            </View>
          ) : null}

          {isLoading && !dashboard ? <LoadingState label="Loading settings..." /> : null}

          {error ? (
            <ErrorState
              title="Couldn't save settings."
              body={error}
              actionLabel="Dismiss"
              onAction={() => setError(null)}
            />
          ) : null}

          {dashboard ? (
            <View className={isDesktop ? 'pt-8' : 'px-4 pt-6'}>
              <Card tone="default" className="gap-4 p-5">
                <View className="flex-row items-center gap-4">
                  <SettingsAvatar
                    avatarURL={dashboard.profile.avatar_url}
                    displayName={dashboard.profile.display_name}
                  />
                  <View className="flex-1">
                    <Text className="text-2xl font-extrabold tracking-tight text-foreground">
                      {dashboard.profile.display_name}
                    </Text>
                    <UsernameBadge
                      username={dashboard.profile.username}
                      isStaff={isStaff}
                      className="mt-1"
                      textClassName="font-semibold text-muted-foreground"
                    />
                  </View>
                </View>
                <Text variant="muted" className="leading-6">
                  {isVenueAccount
                    ? 'Manage your venue page, appearance, and account actions from one place.'
                    : 'Manage your profile, appearance, privacy, and account actions from one place.'}
                </Text>
              </Card>

              <SettingsSection title="Profile" className="mt-6">
                <SettingsRow
                  icon={PencilLineIcon}
                  label="Edit Profile"
                  onPress={() => {
                    setDraftUsername(dashboard.profile.username);
                    setDraftDisplayName(dashboard.profile.display_name);
                    setDraftBio(dashboard.profile.bio ?? '');
                    setDraftAvatarUrl(null);
                    setError(null);
                    setIsEditVisible(true);
                  }}
                />
                <SettingsRow
                  icon={Globe2Icon}
                  label={isVenueAccount ? 'View live venue page' : 'View public'}
                  onPress={() => router.navigate(getPublicIdentityHref(dashboard.profile))}
                />
              </SettingsSection>

              <SettingsSection title="Preferences" className="mt-6">
                <SettingsRow
                  icon={BellRingIcon}
                  label="Notification Preferences"
                  onPress={() => router.push('/settings-notifications')}
                />
                <SettingsRow
                  icon={SparklesIcon}
                  label="Discover interests"
                  detail={formatInterestSummary(dashboard.profile.interest_tags)}
                  onPress={() => {
                    setDraftInterestTags(dashboard.profile.interest_tags ?? []);
                    setError(null);
                    setIsInterestsVisible(true);
                  }}
                />
                <SettingsRow
                  icon={ShieldIcon}
                  label="Privacy"
                  onPress={() => router.push('/settings-privacy')}
                />
                <SettingsRow
                  icon={MonitorIcon}
                  label={getAppearanceSummaryLabel(appearancePreference)}
                  onPress={() => setIsAppearanceVisible(true)}
                />
              </SettingsSection>

              {workspaceMode.orgAccessEnabled ? (
                <SettingsSection title="Workspaces" className="mt-6">
                  <SettingsRow
                    icon={BriefcaseBusinessIcon}
                    label="Choose view"
                    detail={
                      currentContext?.kind === 'business' && currentBusinessMembership
                        ? `Current: ${currentBusinessMembership.organization_name}`
                        : meaningfulContextCount > 1
                          ? 'Current: Personal'
                          : 'Personal only'
                    }
                    onPress={() => router.push('/workspaces' as never)}
                  />
                  {currentContext?.kind === 'business' &&
                  currentBusinessMembership &&
                  (currentBusinessMembership.role === 'owner' || currentBusinessMembership.role === 'manager') ? (
                    <SettingsRow
                      icon={HistoryIcon}
                      label="Activity log"
                      detail={currentBusinessMembership.organization_name}
                      onPress={() => router.push('/(business)/activity' as never)}
                    />
                  ) : null}
                </SettingsSection>
              ) : null}

              <SettingsSection title="Support" className="mt-6">
                <SettingsRow
                  icon={MailIcon}
                  label="Support"
                  onPress={() => Linking.openURL(APP_CONFIG.supportURL).catch(() => undefined)}
                />
                <SettingsRow
                  icon={FileTextIcon}
                  label="Terms of Service"
                  onPress={() => router.push('/terms')}
                />
              </SettingsSection>

              <SettingsSection title="Account" className="mt-6">
                {showSetPassword ? (
                  <SettingsRow
                    icon={LockIcon}
                    label="Set password"
                    onPress={() => {
                      setSetPasswordError(null);
                      setIsSetPasswordVisible(true);
                    }}
                  />
                ) : null}
                <SettingsRow
                  icon={LogOutIcon}
                  label="Log out"
                  destructive
                  onPress={async () => {
                    try {
                      await logout();
                    } finally {
                      showToast({ message: 'Signed out. Browsing as guest.' });
                      router.replace('/(tabs)/events');
                    }
                  }}
                />
                <SettingsRow
                  icon={Trash2Icon}
                  label="Delete account"
                  destructive
                  onPress={() => {
                    Alert.alert(
                      'Delete account',
                      'This permanently deletes your account, all your events, RSVPs, comments, and followers. This cannot be undone.',
                      [
                        { text: 'Cancel', style: 'cancel' },
                        {
                          text: 'Delete forever',
                          style: 'destructive',
                          onPress: () => deleteAccount().catch(() => undefined),
                        },
                      ]
                    );
                  }}
                />
              </SettingsSection>
            </View>
          ) : null}

          <SettingsFooter className={isDesktop ? 'px-0 pt-12' : 'px-4 pt-12'} />
        </View>
      </ScrollView>

      <EditProfileModal
        visible={isEditVisible}
        username={draftUsername}
        displayName={draftDisplayName}
        bio={draftBio}
        error={error}
        avatarUrl={draftAvatarUrl ?? dashboard?.profile.avatar_url}
        isUploadingAvatar={avatarUploadMutation.isPending}
        isSaving={updateProfileMutation.isPending}
        onChangeUsername={(value) => setDraftUsername(normalizeUsernameInput(value))}
        onChangeDisplayName={(value) =>
          setDraftDisplayName(value.slice(0, ACCOUNT_VALIDATION_LIMITS.displayNameMaxLength))
        }
        onChangeBio={(value) => setDraftBio(value.slice(0, ACCOUNT_VALIDATION_LIMITS.bioMaxLength))}
        onPickAvatar={() => handlePickAvatar().catch(() => undefined)}
        onClose={() => {
          setError(null);
          setIsEditVisible(false);
        }}
        onSave={() => handleSaveProfile().catch(() => undefined)}
      />
      <AppearanceModal
        visible={isAppearanceVisible}
        preference={appearancePreference}
        resolvedMode={resolvedAppearance}
        onClose={() => setIsAppearanceVisible(false)}
        onSelect={(nextPreference) => {
          setAppearancePreference(nextPreference).catch(() => undefined);
          setIsAppearanceVisible(false);
        }}
      />
      <InterestPickerModal
        visible={isInterestsVisible}
        selectedTags={draftInterestTags}
        isSaving={updateProfileMutation.isPending}
        onChangeSelectedTags={setDraftInterestTags}
        onClose={() => {
          setError(null);
          setIsInterestsVisible(false);
        }}
        onSave={() => handleSaveInterests().catch(() => undefined)}
      />
      <SetPasswordModal
        visible={isSetPasswordVisible}
        password={newPassword}
        confirmPassword={confirmNewPassword}
        error={setPasswordError}
        isSaving={setPasswordMutation.isPending}
        onChangePassword={setNewPassword}
        onChangeConfirmPassword={setConfirmNewPassword}
        onClose={closeSetPasswordModal}
        onSave={() => handleSetPassword().catch(() => undefined)}
      />
    </AppScreen>
  );
}

function SettingsAvatar({ avatarURL, displayName }: { avatarURL?: string; displayName: string }) {
  return (
    <Avatar
      style={{
        width: 72,
        height: 72,
        borderRadius: 36,
        borderWidth: 2,
        borderColor: DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: DESIGN_TOKENS.colors.surfaceAccent,
      }}>
      {avatarURL ? (
        <AvatarImage source={{ uri: avatarURL }} style={{ width: 72, height: 72 }} />
      ) : (
        <AvatarFallback className="bg-transparent">
          <Text className="text-2xl font-extrabold tracking-tight text-foreground">
            {getProfileInitials(displayName)}
          </Text>
        </AvatarFallback>
      )}
    </Avatar>
  );
}

function SettingsSection({
  title,
  className,
  children,
}: {
  title: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <View className={className}>
      <Text variant="small" className="mb-3 px-1 uppercase tracking-[1.6px] text-muted-foreground">
        {title}
      </Text>
      <Card tone="default" className="overflow-hidden p-0">
        {children}
      </Card>
    </View>
  );
}

function SettingsRow({
  icon,
  label,
  detail,
  onPress,
  destructive = false,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  detail?: string;
  onPress: () => void;
  destructive?: boolean;
}) {
  return (
    <Pressable
      className="flex-row items-center gap-3 border-b border-border px-5 py-4 last:border-b-0"
      onPress={onPress}>
      <Icon as={icon} size={17} className={destructive ? 'text-primary' : 'text-foreground'} />
      <View className="flex-1">
        <Text className={destructive ? 'font-medium text-primary' : 'font-medium text-foreground'}>
          {label}
        </Text>
        {detail ? (
          <Text variant="small" className="mt-1 text-muted-foreground">
            {detail}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}

function SettingsFooter({ className }: { className?: string }) {
  return (
    <View className={className}>
      <View className="items-center gap-1 pb-2">
        <Text variant="small" className="text-sm font-medium text-muted-foreground">
          {APP_BUILD ? `Version ${APP_VERSION} (${APP_BUILD})` : `Version ${APP_VERSION}`}
        </Text>
        <Text variant="small" className="text-center text-xs text-muted-foreground/75">
          {`© ${new Date().getFullYear()} Radious • What’s in your radious?`}
        </Text>
      </View>
    </View>
  );
}

function AppearanceModal({
  visible,
  preference,
  resolvedMode,
  onClose,
  onSelect,
}: {
  visible: boolean;
  preference: AppearancePreference;
  resolvedMode: 'day' | 'night';
  onClose: () => void;
  onSelect: (preference: AppearancePreference) => void;
}) {
  const isDesktop = useIsDesktop();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.48)',
          justifyContent: isDesktop ? 'center' : 'flex-end',
          alignItems: isDesktop ? 'center' : 'stretch',
          paddingHorizontal: isDesktop ? 0 : 16,
          paddingBottom: isDesktop ? 0 : 32,
        }}
        onPress={onClose}>
        <Pressable onPress={(event) => event.stopPropagation()}>
          <Card
            tone="overlay"
            style={isDesktop ? { width: 420, borderRadius: 20 } : undefined}
            className="p-4">
            <View className="flex-row items-center justify-between">
              <View className="flex-1 pr-4">
                <Text variant="large" className="text-xl font-bold">
                  Theme
                </Text>
                <Text variant="small" className="mt-1 text-muted-foreground">
                  {preference === 'auto'
                    ? `Auto is on — currently in ${resolvedMode} mode.`
                    : 'Choose the vibe that fits the moment.'}
                </Text>
              </View>
              <Pressable onPress={onClose}>
                <Text variant="small" className="text-muted-foreground">
                  Close
                </Text>
              </Pressable>
            </View>

            <View className="mt-5 gap-3">
              <AppearanceOption
                icon={MonitorIcon}
                label="Auto"
                description={`Switches by local time. Currently ${resolvedMode}.`}
                selected={preference === 'auto'}
                onPress={() => onSelect('auto')}
              />
              <AppearanceOption
                icon={SunIcon}
                label="Day"
                description="Warm, brighter, and made for daytime browsing."
                selected={preference === 'day'}
                onPress={() => onSelect('day')}
              />
              <AppearanceOption
                icon={MoonIcon}
                label="Night"
                description="Keeps the darker nightlife mood all the time."
                selected={preference === 'night'}
                onPress={() => onSelect('night')}
              />
            </View>
          </Card>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function AppearanceOption({
  icon,
  label,
  description,
  selected,
  onPress,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  description: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      className="rounded-[18px] border p-4"
      style={{
        borderColor: selected ? DESIGN_TOKENS.colors.accentEdge : DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: selected
          ? DESIGN_TOKENS.colors.accentSoft
          : DESIGN_TOKENS.colors.surfaceAlt,
      }}
      onPress={onPress}>
      <View className="flex-row items-start gap-3">
        <View
          className="mt-0.5 size-10 items-center justify-center rounded-full"
          style={{
            backgroundColor: selected
              ? DESIGN_TOKENS.colors.accent
              : DESIGN_TOKENS.colors.canvasInset,
          }}>
          <Icon
            as={icon}
            size={18}
            color={selected ? DESIGN_TOKENS.colors.textInverse : DESIGN_TOKENS.colors.text}
          />
        </View>
        <View className="flex-1">
          <Text className="font-semibold text-foreground">{label}</Text>
          <Text variant="small" className="mt-1 text-muted-foreground">
            {description}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

function getAppearanceSummaryLabel(preference: AppearancePreference) {
  if (preference === 'auto') return 'Theme · Auto';
  if (preference === 'day') return 'Theme · Day';
  return 'Theme · Night';
}

function formatInterestSummary(tags?: string[] | null) {
  if (!tags?.length) {
    return 'Tune Discover around what you like';
  }
  return tags.slice(0, 3).map(getEventTagLabel).join(', ') + (tags.length > 3 ? ` +${tags.length - 3}` : '');
}

function InterestPickerModal({
  visible,
  selectedTags,
  isSaving,
  onChangeSelectedTags,
  onClose,
  onSave,
}: {
  visible: boolean;
  selectedTags: string[];
  isSaving: boolean;
  onChangeSelectedTags: (tags: string[]) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const isDesktop = useIsDesktop();
  const { height } = useWindowDimensions();
  const modalMaxHeight = Math.min(height - (isDesktop ? 96 : 64), isDesktop ? 680 : 620);
  const scrollMaxHeight = Math.max(260, modalMaxHeight - 172);
  const selectedSet = React.useMemo(() => new Set(selectedTags), [selectedTags]);

  function toggleTag(tag: EventTagValue) {
    if (selectedSet.has(tag)) {
      onChangeSelectedTags(selectedTags.filter((value) => value !== tag));
      return;
    }
    if (selectedTags.length >= 12) {
      showToast({ message: 'Pick up to 12 interests for now.' });
      return;
    }
    onChangeSelectedTags([...selectedTags, tag]);
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.70)',
          justifyContent: isDesktop ? 'center' : 'flex-end',
          alignItems: isDesktop ? 'center' : 'stretch',
          paddingHorizontal: isDesktop ? 0 : 16,
          paddingBottom: isDesktop ? 0 : 32,
        }}
        onPress={onClose}>
        <Pressable onPress={(event) => event.stopPropagation()}>
          <Card
            tone="overlay"
            style={isDesktop ? { width: 520, maxHeight: modalMaxHeight, borderRadius: 18 } : { maxHeight: modalMaxHeight, borderRadius: 18 }}
            className="overflow-hidden p-0">
            <View className="flex-row items-center justify-between px-4 pb-4 pt-4">
              <View className="flex-1 pr-4">
                <Text variant="large" className="text-xl font-bold">
                  Discover interests
                </Text>
                <Text variant="small" className="mt-1 text-muted-foreground">
                  Choose the things you want Radious to look for first.
                </Text>
              </View>
              <Pressable onPress={onClose}>
                <Text variant="small" className="text-muted-foreground">
                  Close
                </Text>
              </Pressable>
            </View>

            <ScrollView
              nestedScrollEnabled
              showsVerticalScrollIndicator={false}
              style={{ maxHeight: scrollMaxHeight }}
              contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 8 }}>
              <View className="gap-5 border-t border-border pt-4">
                {EVENT_TAG_GROUPS.map((group) => (
                  <View key={group.key} className="gap-3">
                    <Text variant="small" className="uppercase tracking-[1.4px] text-muted-foreground">
                      {group.label}
                    </Text>
                    <View className="flex-row flex-wrap gap-2">
                      {group.tags.map((tag) => {
                        const selected = selectedSet.has(tag.value);
                        const color = getEventTagColors(tag.value)[0];
                        return (
                          <Pressable
                            key={tag.value}
                            className="flex-row items-center gap-2 rounded-full border py-2 pl-2 pr-3"
                            style={{
                              borderColor: selected
                                ? color
                                : DESIGN_TOKENS.colors.borderStrong,
                              backgroundColor: selected
                                ? `${color}24`
                                : DESIGN_TOKENS.colors.surfaceAlt,
                            }}
                            onPress={() => toggleTag(tag.value)}>
                            <View
                              className="size-3 rounded-full"
                              style={{
                                backgroundColor: color,
                                opacity: selected ? 1 : 0.65,
                              }}
                            />
                            <Text
                              variant="small"
                              className={selected ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'}>
                              {tag.label}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>
                ))}
              </View>
            </ScrollView>

            <View className="flex-row items-center gap-3 border-t border-border px-4 pb-4 pt-4">
              <Pressable
                className="h-12 flex-1 items-center justify-center rounded-[14px] border border-border"
                onPress={() => onChangeSelectedTags([])}>
                <Text className="font-semibold text-foreground">Clear</Text>
              </Pressable>
              <Pressable
                className="h-12 flex-1 items-center justify-center rounded-[14px]"
                style={{ backgroundColor: DESIGN_TOKENS.colors.accent }}
                disabled={isSaving}
                onPress={onSave}>
                <Text className="font-semibold text-primary-foreground">
                  {isSaving ? 'Saving...' : 'Save'}
                </Text>
              </Pressable>
            </View>
          </Card>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function SetPasswordModal({
  visible,
  password,
  confirmPassword,
  error,
  isSaving,
  onChangePassword,
  onChangeConfirmPassword,
  onClose,
  onSave,
}: {
  visible: boolean;
  password: string;
  confirmPassword: string;
  error: string | null;
  isSaving: boolean;
  onChangePassword: (value: string) => void;
  onChangeConfirmPassword: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const isDesktop = useIsDesktop();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.70)',
          justifyContent: isDesktop ? 'center' : 'flex-end',
          alignItems: isDesktop ? 'center' : 'stretch',
          paddingHorizontal: isDesktop ? 0 : 16,
          paddingBottom: isDesktop ? 0 : 32,
        }}
        onPress={onClose}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <Pressable onPress={(event) => event.stopPropagation()}>
            <Card
              tone="overlay"
              style={isDesktop ? { width: 440, borderRadius: 20 } : undefined}
              className="p-4">
              <View className="flex-row items-center justify-between">
                <View className="flex-1 pr-4">
                  <Text variant="large" className="text-xl font-bold">
                    Set Password
                  </Text>
                  <Text variant="small" className="mt-1 text-muted-foreground">
                    Add email and password sign-in without removing Google or Apple access.
                  </Text>
                </View>
                <Pressable onPress={onClose}>
                  <Text variant="small" className="text-muted-foreground">
                    Close
                  </Text>
                </Pressable>
              </View>

              <View className="mt-5 gap-4">
                <View className="gap-2">
                  <Text variant="small" className="text-muted-foreground">
                    New password
                  </Text>
                  <TextInput
                    value={password}
                    onChangeText={onChangePassword}
                    secureTextEntry
                    autoCapitalize="none"
                    autoCorrect={false}
                    placeholder="New password"
                    placeholderTextColor={DESIGN_TOKENS.colors.textMuted}
                    className="rounded-[14px] p-4 text-[16px]"
                    style={{
                      borderWidth: 1,
                      borderColor: DESIGN_TOKENS.colors.borderStrong,
                      backgroundColor: DESIGN_TOKENS.colors.canvasInset,
                      color: DESIGN_TOKENS.colors.text,
                    }}
                  />
                  <Text variant="small" className="text-muted-foreground">
                    Use at least {ACCOUNT_VALIDATION_LIMITS.passwordMinLength} characters.
                  </Text>
                </View>

                <View className="gap-2">
                  <Text variant="small" className="text-muted-foreground">
                    Confirm password
                  </Text>
                  <TextInput
                    value={confirmPassword}
                    onChangeText={onChangeConfirmPassword}
                    secureTextEntry
                    autoCapitalize="none"
                    autoCorrect={false}
                    placeholder="Confirm new password"
                    placeholderTextColor={DESIGN_TOKENS.colors.textMuted}
                    className="rounded-[14px] p-4 text-[16px]"
                    style={{
                      borderWidth: 1,
                      borderColor: DESIGN_TOKENS.colors.borderStrong,
                      backgroundColor: DESIGN_TOKENS.colors.canvasInset,
                      color: DESIGN_TOKENS.colors.text,
                    }}
                  />
                </View>

                {error ? (
                  <Text variant="small" className="text-primary">
                    {error}
                  </Text>
                ) : null}
              </View>

              <Pressable
                className="mt-5 h-12 items-center justify-center rounded-[14px]"
                style={{ backgroundColor: DESIGN_TOKENS.colors.accent }}
                disabled={isSaving}
                onPress={onSave}>
                <Text className="font-semibold text-primary-foreground">
                  {isSaving ? 'Saving...' : 'Set Password'}
                </Text>
              </Pressable>
            </Card>
          </Pressable>
        </KeyboardAvoidingView>
      </Pressable>
    </Modal>
  );
}

function EditProfileModal({
  visible,
  username,
  displayName,
  bio,
  error,
  avatarUrl,
  isUploadingAvatar,
  isSaving,
  onChangeUsername,
  onChangeDisplayName,
  onChangeBio,
  onPickAvatar,
  onClose,
  onSave,
}: {
  visible: boolean;
  username: string;
  displayName: string;
  bio: string;
  error: string | null;
  avatarUrl?: string;
  isUploadingAvatar: boolean;
  isSaving: boolean;
  onChangeUsername: (value: string) => void;
  onChangeDisplayName: (value: string) => void;
  onChangeBio: (value: string) => void;
  onPickAvatar: () => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const isDesktop = useIsDesktop();
  const usernameValidationError = username.trim() ? validateUsername(username) : null;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.70)',
          justifyContent: isDesktop ? 'center' : 'flex-end',
          alignItems: isDesktop ? 'center' : 'stretch',
          paddingHorizontal: isDesktop ? 0 : 16,
          paddingBottom: isDesktop ? 0 : 32,
        }}
        onPress={onClose}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <Pressable onPress={(e) => e.stopPropagation()}>
            <Card
              tone="overlay"
              style={isDesktop ? { width: 480, borderRadius: 20 } : undefined}
              className="p-4">
              <View className="flex-row items-center justify-between">
                <Text variant="large" className="text-xl font-bold">
                  Edit Profile
                </Text>
                <Pressable onPress={onClose}>
                  <Text variant="small" className="text-muted-foreground">
                    Close
                  </Text>
                </Pressable>
              </View>

              <View className="mt-5 gap-4">
                <View className="items-center">
                  <Pressable
                    onPress={onPickAvatar}
                    disabled={isUploadingAvatar}
                    style={{ position: 'relative' }}>
                    <Avatar
                      style={{
                        width: 88,
                        height: 88,
                        borderRadius: 44,
                        borderWidth: 2,
                        borderColor: DESIGN_TOKENS.colors.borderStrong,
                        backgroundColor: DESIGN_TOKENS.colors.surfaceAccent,
                      }}>
                      {isUploadingAvatar ? (
                        <ActivityIndicator color={DESIGN_TOKENS.colors.text} />
                      ) : avatarUrl ? (
                        <AvatarImage
                          source={{ uri: avatarUrl }}
                          style={{ width: 88, height: 88 }}
                        />
                      ) : (
                        <AvatarFallback className="bg-transparent">
                          <Text
                            variant="large"
                            className="text-xl font-semibold text-primary-foreground">
                            {getProfileInitials(displayName || 'U')}
                          </Text>
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <View
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: 28,
                        height: 28,
                        borderRadius: 14,
                        backgroundColor: DESIGN_TOKENS.colors.accent,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <Icon as={CameraIcon} size={14} color="#0d0d0d" />
                    </View>
                  </Pressable>
                  <Text variant="small" className="mt-2 text-muted-foreground">
                    Tap to change photo
                  </Text>
                </View>

                <View className="gap-2">
                  <Text variant="small" className="text-muted-foreground">
                    Username
                  </Text>
                  <TextInput
                    value={username}
                    onChangeText={onChangeUsername}
                    autoCapitalize="none"
                    autoCorrect={false}
                    placeholder="username"
                    placeholderTextColor={DESIGN_TOKENS.colors.textMuted}
                    maxLength={ACCOUNT_VALIDATION_LIMITS.usernameMaxLength}
                    className="rounded-[14px] p-4 text-[16px]"
                    style={{
                      borderWidth: 1,
                      borderColor: DESIGN_TOKENS.colors.borderStrong,
                      backgroundColor: DESIGN_TOKENS.colors.canvasInset,
                      color: DESIGN_TOKENS.colors.text,
                    }}
                  />
                  <Text
                    variant="small"
                    className={cn(
                      usernameValidationError ? 'text-destructive' : 'text-muted-foreground'
                    )}>
                    {usernameValidationError ?? USERNAME_HELP_COPY}
                  </Text>
                </View>

                <View className="gap-2">
                  <Text variant="small" className="text-muted-foreground">
                    Display name
                  </Text>
                  <TextInput
                    value={displayName}
                    onChangeText={onChangeDisplayName}
                    placeholder="Display name"
                    placeholderTextColor={DESIGN_TOKENS.colors.textMuted}
                    maxLength={ACCOUNT_VALIDATION_LIMITS.displayNameMaxLength}
                    className="rounded-[14px] p-4 text-[16px]"
                    style={{
                      borderWidth: 1,
                      borderColor: DESIGN_TOKENS.colors.borderStrong,
                      backgroundColor: DESIGN_TOKENS.colors.canvasInset,
                      color: DESIGN_TOKENS.colors.text,
                    }}
                  />
                </View>

                <View className="gap-2">
                  <Text variant="small" className="text-muted-foreground">
                    Bio
                  </Text>
                  <TextInput
                    value={bio}
                    onChangeText={onChangeBio}
                    placeholder="Tell people what you're into"
                    placeholderTextColor={DESIGN_TOKENS.colors.textMuted}
                    multiline
                    textAlignVertical="top"
                    maxLength={ACCOUNT_VALIDATION_LIMITS.bioMaxLength}
                    className="min-h-[120px] rounded-[14px] p-4 text-[16px]"
                    style={{
                      borderWidth: 1,
                      borderColor: DESIGN_TOKENS.colors.borderStrong,
                      backgroundColor: DESIGN_TOKENS.colors.canvasInset,
                      color: DESIGN_TOKENS.colors.text,
                    }}
                  />
                  <Text variant="small" className="self-end text-muted-foreground">
                    {bio.length}/{ACCOUNT_VALIDATION_LIMITS.bioMaxLength}
                  </Text>
                </View>

                {error ? (
                  <Text variant="small" className="text-primary">
                    {error}
                  </Text>
                ) : null}
              </View>

              <Pressable
                className="mt-5 h-12 items-center justify-center rounded-[14px]"
                style={{ backgroundColor: DESIGN_TOKENS.colors.accent }}
                disabled={isSaving}
                onPress={onSave}>
                <Text className="font-semibold text-primary-foreground">
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Text>
              </Pressable>
            </Card>
          </Pressable>
        </KeyboardAvoidingView>
      </Pressable>
    </Modal>
  );
}
